import React from 'react';
import { useQuery } from 'react-query';
import {
  Button,
  Card,
  CardContent,
  InputBase,
  Typography,
} from '@material-ui/core';
import dayjs from 'dayjs';
import Loading from 'components/atoms/Loading';
import { IHistory } from 'interfaces/StockPosition';
import { getEntryHistory } from 'services/stock-position';
import BaseModal from 'components/molecules/AnomalyGrid/components/BaseModal';
import { IModalEntryHistoryProps } from './types';
import useStyles from './style';

const ModalEntryHistory = ({
  history,
  gridRow,
  children,
}: IModalEntryHistoryProps) => {
  const classes = useStyles();
  const [open, setOpen] = React.useState<boolean>(false);
  const [histories, setHistories] = React.useState<IHistory[]>([]);

  const onOpen = React.useCallback(() => setOpen(true), []);
  const onClose = React.useCallback(() => setOpen(false), []);

  const gridId = React.useMemo(() => {
    return gridRow?.id;
  }, [gridRow?.id]);

  const enableQueryEntryHistory = React.useMemo<boolean>(() => {
    return Boolean(gridId) && open;
  }, [gridId, open]);

  const queryEntryHistory = useQuery({
    queryKey: [`EntryHistoryContext${gridId}`],
    queryFn: async () => {
      const { data } = await getEntryHistory(gridId);
      return data;
    },
    onSuccess: data => {
      setHistories(data);
    },
    enabled: enableQueryEntryHistory,
    cacheTime: 0,
  });

  const renderBody = React.useCallback(() => {
    if (queryEntryHistory?.isLoading) {
      return <Loading />;
    }

    if (histories.length === 0) {
      return (
        <div className={classes.historyContent}>
          <Typography className={classes.defaultMessage}>
            Não possui histórico de entrada.
          </Typography>
        </div>
      );
    }

    return (
      <div className={classes.historyContent}>
        {histories.map(({ user, inputDate, movementDate }) => (
          <div className={classes.historyItem}>
            <Card className={classes.cardRoot} elevation={0}>
              <CardContent>
                <div className={classes.cardHeader}>
                  <Typography
                    className={classes.cardHeaderText}
                    color="textSecondary"
                    gutterBottom
                  >
                    {user}
                  </Typography>
                  <Typography
                    className={classes.cardHeaderText}
                    color="textSecondary"
                    gutterBottom
                  >
                    {dayjs(inputDate).format('DD/MM/YY')}
                  </Typography>
                </div>
                <InputBase
                  className={classes.historyText}
                  style={{ width: '100%' }}
                  id="history-text"
                  disabled
                  multiline
                  value={dayjs(movementDate).format('DD/MM/YY')}
                  rowsMax={4}
                  defaultValue={history}
                />
              </CardContent>
            </Card>
          </div>
        ))}
      </div>
    );
  }, [
    queryEntryHistory?.isLoading,
    history,
    classes.historyContent,
    classes.defaultMessage,
    classes.historyItem,
    classes.cardRoot,
    classes.cardHeader,
    classes.cardHeaderText,
    classes.historyText,
    histories,
  ]);

  const renderLink = React.useCallback(() => {
    return (
      <div
        onClick={onOpen}
        aria-hidden="true"
        role="button"
        className={classes.cellData}
      >
        {children}
      </div>
    );
  }, [children, classes.cellData, onOpen]);

  const renderFooter = React.useCallback(() => {
    return (
      <Button color="primary" onClick={onClose}>
        Fechar
      </Button>
    );
  }, [onClose]);

  return (
    <BaseModal
      title="Histórico de Data de Entrada"
      body={renderBody()}
      footer={renderFooter()}
      open={open}
      onClose={onClose}
      width="492px"
    >
      {renderLink()}
    </BaseModal>
  );
};

export default ModalEntryHistory;
